import React from 'react';
import { CharacterData } from '../../types/character';
import { getProficiency, isProficiencyAtMax, getMinimumProficiency } from '../../utils/weaponDamage';
import styles from './ProficiencyDisplay.module.css';

interface ProficiencyDisplayProps {
  character: CharacterData;
}

/**
 * ProficiencyDisplay Component
 *
 * Displays character proficiency as:
 * - Label as heading (PROFICIENCY)
 * - Visual circles (● for filled, ○ for empty)
 * - Warning if proficiency is below expected for level
 *
 * Example:
 * ┌─────────────────────────────────────┐
 * │ PROFICIENCY                     MAX  │
 * │       ●●●●●●                       │
 * │                                     │
 * │ ⚠️  Expected minimum proficiency... │
 * └─────────────────────────────────────┘
 */
export const ProficiencyDisplay: React.FC<ProficiencyDisplayProps> = ({ character }) => {
  const proficiency = getProficiency(character);
  const isMax = isProficiencyAtMax(proficiency);
  const characterLevel = parseInt(String(character.level || 1), 10);
  const minProficiency = getMinimumProficiency(characterLevel);
  const isBelowExpected = proficiency < minProficiency;

  return (
    <div className={styles.proficiencyContainer}>
      <div className={styles.labelRow}>
        <label className={styles.label}>PROFICIENCY</label>
        {isMax && <span className={styles.maxBadge}>MAX</span>}
      </div>

      <div className={styles.valueCard}>
        <div className={styles.circles}>
          {[1, 2, 3, 4, 5, 6].map((level) => (
            <div
              key={level}
              className={`${styles.circle} ${
                level <= proficiency ? styles.filled : styles.empty
              }`}
              aria-label={`Proficiency level ${level}`}
            />
          ))}
        </div>
      </div>

      {isBelowExpected && (
        <div className={styles.warning}>
          <span className={styles.warningIcon}>⚠️</span>
          <span className={styles.warningText}>
            Expected minimum proficiency {minProficiency} for level {characterLevel}
          </span>
        </div>
      )}
    </div>
  );
};

export default ProficiencyDisplay;
